import { nextTick } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import { useUserStore } from '@provise/stores/user'
import Home from '@/views/home.vue'
import Login from '@provise/views/login.vue'
import SsoLogin from '@provise/views/login/sso.vue'
import config from '@/config'
import { storeToRefs } from 'pinia'

const router = createRouter({
  history: createWebHistory(config.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: Home,
      meta: {
        title: 'Сводка',
        navbar: true,
      },
    },
    {
      path: '/login',
      name: 'login',
      component: Login,
    },
    {
      path: '/activities',
      name: 'activities.list',
      component: () => import('@/views/activities/list.vue'),
      meta: {
        title: 'Планирование',
        navbar: true,
        rbac: 'activities_view',
      },
    },
    {
      path: '/activities/:id(\\d+)?',
      name: 'activities.item',
      component: () => import('@/views/activities/item.vue'),
      props: rt => ({ id: Number(rt.params.id) }),
      meta: { title: 'План' },
    },
    {
      path: '/orders',
      name: 'orders.list',
      component: () => import('@/views/orders/list.vue'),
      meta: {
        title: 'Заявки',
        navbar: true,
        rbac: 'orders_view',
      },
    },
    {
      path: '/orders/:id(\\d+)',
      name: 'orders.item',
      component: () => import('@/views/orders/item.vue'),
      props: rt => ({ id: Number(rt.params.id), newOrder: rt.query.newOrder }),
      meta: { title: 'Заявка' },
    },
    {
      path: '/orders/:id(\\d+)/cost',
      name: 'orders.item.cost',
      component: () => import('@/views/orders/item/cost.vue'),
      props: rt => ({ id: Number(rt.params.id) }),
      meta: { title: 'Оценка тестирования', rbac: ['buh', 'coordinator'] },
    },
    {
      path: '/stock',
      name: 'stock.list',
      meta: {
        title: 'Склад',
        navbar: true,
        rbac: 'stock_view',
      },
      redirect: { name: 'stock.medicines.list' },
      children: [
        {
          path: '/stock/medicines',
          name: 'stock.medicines.list',
          component: () => import('@/views/stock/medicines/list.vue'),
          meta: {
            title: 'Лекарственные препараты',
            navbar: true,
            rbac: 'stock_view',
          },
        },
        {
          path: '/stock/medicines/:id(\\d+)?',
          name: 'stock.medicines.item',
          component: () => import('@/views/stock/medicines/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: { title: 'Лекарственные препараты' },
        },
        {
          path: '/stock/materials_nd',
          name: 'stock.materials_nd.list',
          component: () => import('@/views/stock/materials_nd/list.vue'),
          meta: {
            title: 'Реагенты внутренние',
            navbar: true,
            rbac: 'stock_view',
          },
        },
        {
          path: '/stock/materials_nd/:id(\\d+)?',
          name: 'stock.materials_nd.item',
          component: () => import('@/views/stock/materials_nd/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: { title: 'Реагент внутренний' },
        },
        {
          path: '/stock/materials_comm',
          name: 'stock.materials_comm.list',
          component: () => import('@/views/stock/materials_comm/list.vue'),
          meta: {
            title: 'Реагенты внешние',
            navbar: true,
            rbac: 'stock_view',
          },
        },
        {
          path: '/stock/materials_comm/:id(\\d+)?',
          name: 'stock.materials_comm.item',
          component: () => import('@/views/stock/materials_comm/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: { title: 'Реагент внешний' },
        },
        {
          path: '/stock/devices',
          name: 'stock.devices.list',
          component: () => import('@/views/stock/devices/list.vue'),
          meta: {
            title: 'Колонки и устройства',
            navbar: true,
            rbac: 'stock_view',
          },
        },
        {
          path: '/stock/devices/:id(\\d+)?',
          name: 'stock.devices.item',
          component: () => import('@/views/stock/devices/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: { title: 'Колонка или устройство' },
        },
        {
          path: '/stock/consumables',
          name: 'stock.consumables.list',
          component: () => import('@/views/stock/consumables/list.vue'),
          meta: {
            title: 'Расходные материалы',
            navbar: true,
            rbac: 'stock_view',
          },
        },
        {
          path: '/stock/consumables/:id(\\d+)?',
          name: 'stock.consumables.item',
          component: () => import('@/views/stock/consumables/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: { title: 'Расходный материал' },
        },
      ],
    },
    {
      path: '/catalog',
      name: 'catalog.list',
      meta: {
        title: 'Справочник',
        navbar: true,
        rbac: 'catalog_view',
      },
      redirect: { name: 'catalog.medicine_trademarks.list' },
      children: [
        {
          path: '/catalog/medicine_trademarks',
          name: 'catalog.medicine_trademarks.list',
          component: () => import('@/views/catalog/medicine_trademarks/list.vue'),
          meta: {
            title: 'Торговое наименование',
            navbar: true,
            rbac: 'catalog_view',
          },
        },
        {
          path: '/catalog/medicine_trademarks/new',
          name: 'catalog.medicine_trademarks.new',
          component: () => import('@/views/catalog/medicine_trademarks/item.vue'),
          props: () => ({ id: null }),
          meta: { title: 'Торговое наименование' },
        },
        {
          path: '/catalog/medicine_trademarks/:id(\\d+)?',
          name: 'catalog.medicine_trademarks.item',
          component: () => import('@/views/catalog/medicine_trademarks/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: { title: 'Торговое наименование' },
        },
        {
          path: '/catalog/medicines',
          name: 'catalog.medicines.list',
          component: () => import('@/views/catalog/medicines/list.vue'),
          meta: {
            title: 'Лекарственные препараты',
            navbar: true,
            rbac: 'catalog_view',
          },
        },
        {
          path: '/catalog/medicines/:id(\\d+)?',
          name: 'catalog.medicines.item',
          component: () => import('@/views/catalog/medicines/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: { title: 'Лекарственные препарат' },
        },
        {
          path: '/catalog/materials_nd',
          name: 'catalog.materials_nd.list',
          component: () => import('@/views/catalog/materials_nd/list.vue'),
          meta: {
            title: 'Реагенты внутренние',
            navbar: true,
            rbac: 'catalog_view',
          },
        },
        {
          path: '/catalog/materials_nd/:id(\\d+)?',
          name: 'catalog.materials_nd.item',
          component: () => import('@/views/catalog/materials_nd/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: { title: 'Реагент внутренний' },
        },
        {
          path: '/catalog/materials_comm',
          name: 'catalog.materials_comm.list',
          component: () => import('@/views/catalog/materials_comm/list.vue'),
          meta: {
            title: 'Реагенты внешние',
            navbar: true,
            rbac: 'catalog_view',
          },
        },
        {
          path: '/catalog/materials_comm/:id(\\d+)?',
          name: 'catalog.materials_comm.item',
          component: () => import('@/views/catalog/materials_comm/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: { title: 'Реагент внешний' },
        },
        {
          path: '/catalog/devices',
          name: 'catalog.devices.list',
          component: () => import('@/views/catalog/devices/list.vue'),
          meta: {
            title: 'Колонки и устройства',
            navbar: true,
            rbac: 'catalog_view',
          },
        },
        {
          path: '/catalog/devices/:id(\\d+)?',
          name: 'catalog.devices.item',
          component: () => import('@/views/catalog/devices/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: { title: 'Колонка или устройство' },
        },
        {
          path: '/catalog/consumables',
          name: 'catalog.consumables.list',
          component: () => import('@/views/catalog/consumables/list.vue'),
          meta: {
            title: 'Расходные материалы',
            navbar: true,
            rbac: 'catalog_view',
          },
        },
        {
          path: '/catalog/consumables/:id(\\d+)?',
          name: 'catalog.consumables.item',
          component: () => import('@/views/catalog/consumables/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: { title: 'Расходный материал' },
        },
        {
          path: '/catalog/vendors',
          name: 'catalog.vendors.list',
          component: () => import('@/views/catalog/vendors/list.vue'),
          meta: {
            title: 'Поставщики',
            navbar: true,
            rbac: 'catalog_view',
          },
        },
        {
          path: '/catalog/vendors/:id(\\d+)?',
          name: 'catalog.vendors.item',
          component: () => import('@/views/catalog/vendors/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: { title: 'Поставщик' },
        },
        {
          path: '/catalog/laboratories',
          name: 'catalog.laboratories.list',
          component: () => import('@/views/catalog/laboratories/list.vue'),
          meta: {
            title: 'Лаборатории',
            navbar: true,
            rbac: 'catalog_view',
          },
        },
        {
          path: '/catalog/laboratories/:id(\\d+)?',
          name: 'catalog.laboratories.item',
          component: () => import('@/views/catalog/laboratories/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: { title: 'Лаборатория' },
        },
        {
          path: '/templates',
          name: 'templates.list',
          component: () => import('@/views/templates/list.vue'),
          meta: {
            title: 'Шаблоны',
            navbar: true,
            rbac: 'templates_view',
          },
        },
        {
          path: '/templates/:id(\\d+)?',
          name: 'templates.item',
          component: () => import('@/views/templates/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: { title: 'Шаблон' },
        },
        {
          path: '/catalog/activity_types',
          name: 'catalog.activity_types.list',
          component: () => import('@/views/catalog/activity_types/list.vue'),
          meta: {
            title: 'Типы назначений',
            navbar: true,
            rbac: 'sa',
          },
        },
        {
          path: '/catalog/activity_types/:id(\\d+)?',
          name: 'catalog.activity_types.item',
          component: () => import('@/views/catalog/activity_types/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: { title: 'Тип назначения' },
        },
        {
          path: '/dropdowns',
          name: 'dropdowns.list',
          component: () => import('@/views/dropdowns/list.vue'),
          meta: {
            title: 'Выпадающие списки',
            navbar: true,
            // rbac: 'sa',
          },
        },
        {
          path: '/users',
          name: 'users.list',
          component: () => import('@provise/views/users/list.vue'),
          meta: {
            title: 'Пользователи',
            navbar: true,
            rbac: 'users_edit',
          },
        },
        {
          path: '/users/:id(\\d+)?',
          name: 'users.item',
          component: () => import('@provise/views/users/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: { title: 'Пользователь' },
        },
      ],
    },
    {
      path: '/current-user/',
      name: 'profile.item',
      component: () => import('@provise/views/users/profile.vue'),
      meta: { title: 'Пользователь' },
    },
    {
      path: '/analitycs',
      name: 'analitycs.list',
      meta: {
        title: 'Аналитика',
        navbar: true,
      },
      redirect: { name: 'analytics.report-1' },
      children: [
        {
          path: '/analitycs/report-1',
          name: 'analitycs.report-1',
          component: () => import('@/views/analitycs/reports.vue'),
          meta: {
            title: 'Отчет 1',
            navbar: true,
          },
        },
        {
          path: '/analitycs/report-2',
          name: 'analitycs.report-2',
          component: () => import('@/views/analitycs/reports.vue'),
          meta: {
            title: 'Отчет 2',
            navbar: true,
          },
        },
      ],
    },
    {
      path: '/documents',
      name: 'documents.list',
      meta: {
        title: 'Документы',
        navbar: true,
        rbac: 'common',
      },
      redirect: { name: 'documents.debiting.list' },
      children: [
        {
          path: '/documents/debiting',
          name: 'documents.debiting.list',
          component: () => import('@/views/documents/debitingList.vue'),
          meta: {
            title: 'Акты списания ТМЦ',
            navbar: true,
            rbac: ['writeoffs_view'],
          },
        },
        {
          path: '/documents/debiting/:id(\\d+)?',
          name: 'documents.debiting.item',
          component: () => import('@/views/documents/debitingItem.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: {
            title: 'Акт списания ТМЦ',
            rbac: ['writeoffs_edit', 'writeoffs_view'],
          },
        },
        {
          path: '/documents/transfer',
          name: 'documents.transfer.list',
          component: () => import('@/views/documents/transferList.vue'),
          meta: {
            title: 'Акты приема-передачи',
            navbar: true,
            rbac: ['acts_pp_view', 'acts_pp_edit'],
          },
        },
      ],
    },
    {
      path: '/sso_login',
      name: 'sso_login',
      component: SsoLogin,
    },
    {
      path: '/:notFoundPath(.*)',
      name: 'NotFound',
      component: () => import('@/views/notFound.vue'),
    },
  ],
  strict: true,
  scrollBehavior(to, from) {
    if (to.name === from.name) {
      return null
    }
    return { top: 0, behavior: 'smooth' }
  },
})

const defaultTitle = 'ARTAS'
router.beforeEach(to => {
  const userStore = useUserStore()
  const { isAuthorized } = storeToRefs(userStore)

  if (isAuthorized.value === true && to.name === 'login') {
    return { name: 'home' }
  }

  if (isAuthorized.value === false && to.name !== 'login') {
    return { name: 'login' }
  }
})

router.afterEach(async to => {
  await nextTick()
  let t = []
  for (let m of to.matched) {
    t = [...t, m.meta.title]
  }
  t.reverse()
  t = [...t, defaultTitle]
  document.title = to.meta.title ? t.join(' / ') : defaultTitle
})

export default router
